import { ReactNode, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import classNames from "classnames";

import ErrorMessage from "../ErrorMessage";
import styles from "./index.module.scss";
import Checkbox from "../Checkbox";
import ToggleSelection from "../ToggleSelection";

type PropsType = {
  section?: string;
  name: string;
  options: Array<{
    label: string;
    name: string;
    sections: Array<string>;
    subOptions?: Array<{
      label: string;
      id: string;
      value: string;
      sections: Array<string>;
    }>;
  }>;
  label?: string;
  description?: string;
  disabled?: boolean;
  requiredText?: string;
  readOnly?: boolean;
  tooltip?: ReactNode;
};

const CheckboxGroup = ({
  section,
  name,
  requiredText,
  options,
  disabled,
  label,
  description,
  readOnly,
  tooltip,
}: PropsType) => {
  const {
    formState: { errors },
    setValue,
  } = useFormContext();

  const nameDotFormat = (() => {
    if (section) {
      return `${section}.${name}`;
    }
    return `${name}`;
  })();

  const error =
    section && errors[section] ? errors[section][name] : errors[name];

  const checkboxOnChange = useWatch({ name: nameDotFormat });
  const selectedValues = useWatch({ name: nameDotFormat });

  const subOptionsWatch = useWatch({
    name: `${nameDotFormat}-subOptions`,
  });

  useEffect(() => {
    // ToDo: Optimise this
    [
      "info.section2a-show",
      "info.section2b-show",
      "info.section2c-show",
      "info.section2d-show",
    ].forEach((section: string) => {
      setValue(section, false);
    });
    options?.forEach((option) => {
      if (checkboxOnChange && checkboxOnChange.includes(option.name) === true) {
        if (option?.subOptions) {
          option?.subOptions.forEach((subOption) => {
            if (subOptionsWatch?.[option.name]?.includes(subOption.id)) {
              subOption.sections.forEach((section: string) => {
                setValue(section, true);
              });
            }
          });
        } else {
          option?.sections.forEach((section: string) => {
            setValue(section, true);
          });
        }
      }
    });
  }, [
    checkboxOnChange,
    nameDotFormat,
    options,
    setValue,
    section,
    subOptionsWatch,
  ]);

  return (
    <div>
      <p className={styles.labelText}>
        {label} {tooltip}
      </p>
      {error && requiredText && (
        <ErrorMessage field={name}>{requiredText}</ErrorMessage>
      )}
      {description && <p className={styles.descriptionText}>{description}</p>}
      <fieldset className={classNames(styles.radioGroupFieldset)}>
        <span key={nameDotFormat}>
          {options.map((option) => {
            const oneItem = option.sections.length === 1; // Empty div for correct positioning if one section only
            return (
              <span key={`${option.name}-span`}>
                <div key={`${option.name}-div`} className={styles.grid}>
                  <Checkbox
                    key={`${nameDotFormat}-${option.name}`}
                    name={nameDotFormat}
                    value={option.name}
                    label={option.label}
                    disabled={
                      disabled ||
                      (readOnly &&
                        (!selectedValues || // selectedValues can be false (if never touched), OR be an array of checked values
                          (selectedValues &&
                            !selectedValues?.includes(option.name))))
                    }
                    requiredText={requiredText}
                    readOnly={
                      readOnly &&
                      (!selectedValues ||
                        (selectedValues &&
                          selectedValues?.includes(option.name)))
                    }
                  />
                  <div className={styles.sectionGrid}>
                    {oneItem && <div />}
                    {option.sections.map((section, index) => (
                      <span key={index.toString()}>
                        {/* Empty div for third section */}
                        {index === 2 && <div />}
                        <div
                          className={classNames(styles.section, {
                            [styles.sectionDisabled]: disabled || readOnly,

                            [styles.sectionSelected]: (() => {
                              if (
                                checkboxOnChange &&
                                checkboxOnChange.includes(option.name)
                              ) {
                                // If no subOptions, then all sections corresponding to that option are enabled
                                if (!option.subOptions) {
                                  return true;
                                }
                                let flag = false;
                                option?.subOptions?.forEach((subOption) => {
                                  if (
                                    subOptionsWatch?.[option.name]?.includes(
                                      subOption?.id
                                    )
                                  ) {
                                    subOption.sections.forEach(
                                      (subOptionSection: string) => {
                                        if (subOptionSection === section) {
                                          flag = true;
                                        }
                                      }
                                    );
                                  }
                                });
                                return flag;
                              }
                              return false;
                            })(),
                          })}
                        >
                          Section{" "}
                          {section
                            .replace("info.section", "")
                            .replace("-show", "")}
                        </div>
                      </span>
                    ))}
                  </div>
                  {!readOnly && (
                    <div className={styles.subOptions}>
                      {option.subOptions &&
                        checkboxOnChange &&
                        checkboxOnChange.includes(option.name) && (
                          <ToggleSelection
                            name={`${nameDotFormat}-subOptions.${option.name}`}
                            options={option.subOptions}
                            small
                          />
                        )}
                    </div>
                  )}
                  <div className={styles.line} />
                </div>
              </span>
            );
          })}
        </span>
      </fieldset>
    </div>
  );
};

export default CheckboxGroup;
